/**
 * What Record keeps on this device (Brian's record.html): the photos and clips
 * behind you, per story, and the takes, per story, until the creator discards
 * them. Never uploaded. IndexedDB 'gogrov-record': 'gallery' keyed by story id,
 * 'takes' auto-numbered with the story on each. Every call fails soft: a
 * private window or blocked storage just keeps nothing.
 */

export interface StoredItem {
  type: 'img' | 'video'
  blob: Blob
  start: number
  end: number
  label: string
  fit: Fit | null
}

/** How a photo or clip is framed behind you: zoom (1 = as it comes) and the centre, 0..1 of the picture. */
export interface Fit { s: number; x: number; y: number }

export interface StoredTake {
  id?: number
  story: string
  at: number
  dur: number
  land: boolean
  mode: 'split' | 'green'
  blob: Blob | null
  poster: string
  mime: string
}

/**
 * GoGrov's space on the phone (Brian, Sep 30: "it should save and force them to remove any if it fills up"):
 * takes and galleries are kept until the creator clears them, never on a timer. A take always saves, even the
 * one that fills it; once full, Record and adding photos stop until some are cleared. 2 GB, as in Library.
 */
export const STORE_BUDGET = 2 * 1024 ** 3
export interface StoreEntry { kind: 'gallery' | 'take'; story: string; n?: number; id?: number; dur?: number; bytes: number }
export const usedBytes = (list: StoreEntry[]) => list.reduce((a, x) => a + x.bytes, 0)
const MB = 1024 ** 2
export const fmtBytes = (n: number) => (n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(2)} GB` : `${Math.max(1, Math.round(n / MB))} MB`)

const open = () => new Promise<IDBDatabase>((res, rej) => {
  const r = indexedDB.open('gogrov-record', 1)
  r.onupgradeneeded = () => {
    r.result.createObjectStore('gallery')
    r.result.createObjectStore('takes', { keyPath: 'id', autoIncrement: true })
  }
  r.onsuccess = () => res(r.result)
  r.onerror = () => rej(r.error)
})

async function tx<T>(store: 'gallery' | 'takes', mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest | void): Promise<T | null> {
  try {
    const db = await open()
    return await new Promise<T | null>((res) => {
      const t = db.transaction(store, mode)
      const r = fn(t.objectStore(store))
      t.oncomplete = () => res(r ? (r.result as T) : null)
      t.onerror = () => res(null)
      t.onabort = () => res(null)
    })
  } catch { return null }
}

export const recordStore = {
  saveGallery: (story: string, items: StoredItem[]) => tx('gallery', 'readwrite', (s) => s.put(items, story)),
  loadGallery: async (story: string) => (await tx<StoredItem[]>('gallery', 'readonly', (s) => s.get(story))) ?? [],
  addTake: (take: StoredTake) => tx<number>('takes', 'readwrite', (s) => s.add(take)),
  deleteTake: (id: number) => tx('takes', 'readwrite', (s) => s.delete(id)),
  takesFor: async (story: string) => ((await tx<StoredTake[]>('takes', 'readonly', (s) => s.getAll())) ?? []).filter((t) => t.story === story),
  /** Every story's gallery and every take, with their sizes: what fills GoGrov's space on this phone. */
  usage: async (): Promise<StoreEntry[]> => {
    const out: StoreEntry[] = []
    let db: IDBDatabase
    try { db = await open() } catch { return out }
    const all = <V>(name: 'gallery' | 'takes') => new Promise<[IDBValidKey, V][]>((res) => {
      const r: [IDBValidKey, V][] = []
      try {
        const c = db.transaction(name, 'readonly').objectStore(name).openCursor()
        c.onsuccess = () => { const k = c.result; if (k) { r.push([k.key, k.value as V]); k.continue() } else res(r) }
        c.onerror = () => res(r)
      } catch { res(r) }
    })
    for (const [story, list] of await all<StoredItem[]>('gallery')) {
      if (list && list.length) out.push({ kind: 'gallery', story: String(story), n: list.length, bytes: list.reduce((a, g) => a + (g.blob?.size || 0), 0) })
    }
    for (const [id, t] of await all<StoredTake>('takes')) out.push({ kind: 'take', story: t.story, id: Number(id), dur: t.dur, bytes: t.blob?.size || 0 })
    return out
  },
  isFull: async () => usedBytes(await recordStore.usage()) >= STORE_BUDGET,
  clearEntry: async (x: StoreEntry) => {
    if (x.kind === 'take' && x.id != null) await recordStore.deleteTake(x.id)
    else if (x.kind === 'gallery') await tx('gallery', 'readwrite', (s) => s.delete(x.story))
  },
  /** "I posted it" with Remove GoGrov's copy: the story's takes and gallery go. */
  clearStory: async (story: string) => {
    const mine = await recordStore.takesFor(story)
    for (const t of mine) if (t.id != null) await recordStore.deleteTake(t.id)
    await tx('gallery', 'readwrite', (s) => s.delete(story))
  },
}

// ---------- preferences kept on this device (Brian's keys) ----------
export const pref = (k: string, d: string): string => {
  try { const v = localStorage.getItem(k); return v == null ? d : v } catch { return d }
}
export const setPref = (k: string, v: string) => { try { localStorage.setItem(k, v) } catch { /* */ } }

// ---------- the creator's own script (v2.1, Oct 22) ----------
// Kept for that story and voice on this device; "Restore the written script" drops it.
const scriptKey = (storyId: string, voice?: string | null) => `gogrov.script.${storyId}.${voice || 'default'}`

/** The script to show: the creator's edit when there is one, else the written one. */
export function scriptFor(story: { id: string; script: string | null; voice?: string | null }): string {
  const own = pref(scriptKey(story.id, story.voice), '')
  return own || story.script || ''
}
export const scriptEdited = (storyId: string, voice?: string | null) => !!pref(scriptKey(storyId, voice), '')
export function setOwnScript(storyId: string, voice: string | null | undefined, text: string | null) {
  try {
    if (text == null) localStorage.removeItem(scriptKey(storyId, voice))
    else localStorage.setItem(scriptKey(storyId, voice), text)
  } catch { /* */ }
}
